import { z } from 'zod';
import type { ImportableScan90Backup } from './types';

const id = z.string().min(1);
const timestamp = z.string().datetime({ offset: true });
const moduleId = z.enum(['read-switch', 'central-peripheral', 'player-track', 'moving-target']);
const sessionMode = z.enum(['familiarization', 'baseline-a', 'baseline-b', 'training', 'reassessment', 'retention']);
const primitive = z.union([z.string(), z.number().finite(), z.boolean(), z.null()]);

const calibration = z.object({
  id,
  createdAt: timestamp,
  pixelsPerCentimeter: z.number().finite().positive(),
  viewingDistanceCm: z.number().finite().positive(),
  method: z.enum(['card', 'ruler']),
  correction: z.enum(['none', 'glasses', 'contacts']),
});

const environment = z.object({
  id,
  conditionId: id.optional(),
  capturedAt: timestamp,
  viewportWidth: z.number().finite().positive(),
  viewportHeight: z.number().finite().positive(),
  devicePixelRatio: z.number().finite().positive(),
  refreshRateHz: z.number().finite().positive(),
  frameIntervalMedianMs: z.number().finite().positive(),
  frameIntervalP95Ms: z.number().finite().positive(),
  userAgent: z.string(),
  inputMethod: z.enum(['pointer', 'keyboard']),
  calibrationId: id.optional(),
});

const session = z.object({
  id,
  startedAt: timestamp,
  completedAt: timestamp.optional(),
  mode: sessionMode,
  protocolVersion: z.string().min(1),
  moduleIds: z.array(moduleId).optional(),
  status: z.enum(['in-progress', 'completed', 'aborted']),
  environmentId: id,
  environmentConditionId: id.optional(),
  fatigue: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
  correctionConfirmed: z.boolean(),
  trialIds: z.array(id).optional(),
});

const trial = z.object({
  id,
  sessionId: id,
  trialId: id,
  moduleId,
  protocolVersion: z.string().min(1),
  seed: z.number().int(),
  stimulusId: id,
  startedAt: timestamp,
  stimulusOnsetMs: z.number().finite().nonnegative(),
  responseAtMs: z.number().finite().nonnegative().optional(),
  response: z.string().optional(),
  correctResponse: z.union([z.string(), z.array(z.string())]),
  correct: z.boolean(),
  // Anticipatory keypresses can precede the response onset; retain their raw
  // signed latency even though they are invalid and excluded from summaries.
  reactionTimeMs: z.number().finite().optional(),
  difficulty: z.number().finite(),
  valid: z.boolean(),
  invalidReason: z.enum(['anticipation', 'timeout', 'focus-lost', 'visibility-hidden', 'viewport-changed', 'fullscreen-exited', 'frame-drop', 'manual-pause', 'session-aborted']).optional(),
  environmentId: id,
  metadata: z.record(z.string(), primitive).optional(),
});

const summary = z.object({
  id,
  sessionId: id,
  moduleId,
  validTrials: z.number().int().nonnegative(),
  accuracy: z.number().min(0).max(1),
  accuracyCi95: z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]).optional(),
  medianReactionTimeMs: z.number().finite().nonnegative().optional(),
  reactionTimeIqrMs: z.tuple([z.number().finite().nonnegative(), z.number().finite().nonnegative()]).optional(),
  omissions: z.number().int().nonnegative(),
  falseResponses: z.number().int().nonnegative(),
  threshold: z.number().finite().nonnegative().optional(),
  thresholdUnit: z.enum(['speed', 'arcmin', 'percent', 'ms']).optional(),
  baselineIndex: z.number().finite().nonnegative().optional(),
  conditionSummaries: z.array(z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    validTrials: z.number().int().nonnegative(),
    accuracy: z.number().min(0).max(1),
    medianReactionTimeMs: z.number().finite().nonnegative().optional(),
  })).optional(),
});

const appSettings = z.object({
  profileName: z.string(),
  age: z.number().int().min(18).max(120),
  position: z.literal('field'),
  onboardingComplete: z.boolean(),
  calibrationId: id.optional(),
  preferredKeys: z.record(z.string(), z.string()),
});

const settingsRecord = z.object({ key: z.literal('app'), value: appSettings, updatedAt: timestamp });
const metadataRecord = z.object({ key: z.string().min(1), value: primitive, updatedAt: timestamp });

const v1 = z.object({
  app: z.literal('SCAN90 LAB'),
  schemaVersion: z.literal(1),
  exportedAt: timestamp,
  data: z.object({
    calibrations: z.array(calibration),
    environments: z.array(environment),
    sessions: z.array(session),
    trials: z.array(trial),
    settings: z.array(settingsRecord).optional(),
  }),
});

const v2 = z.object({
  app: z.literal('SCAN90 LAB'),
  schemaVersion: z.literal(2),
  exportedAt: timestamp,
  data: z.object({
    calibrations: z.array(calibration),
    environments: z.array(environment),
    sessions: z.array(session),
    trials: z.array(trial),
    summaries: z.array(summary),
    settings: z.array(settingsRecord),
    metadata: z.array(metadataRecord),
  }),
});

const importableBackup = z.discriminatedUnion('schemaVersion', [v1, v2]);

export function validateImportableBackup(value: unknown): ImportableScan90Backup {
  const result = importableBackup.safeParse(value);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') || 'root';
    throw new Error(`Invalid backup: ${path} ${first?.message ?? 'failed validation'}`);
  }
  return result.data as ImportableScan90Backup;
}
